<template>
  <div class="order-comparison">
    <div class="module-header">
      <h3 class="module-title">征订订单数及码洋同比</h3>
      <!-- 外部自定义图例：紧邻标题右侧，与标题垂直居中对齐 -->
      <div class="legend-outside" v-if="chart">
        <div 
          class="legend-item legend-order-count" 
          :class="{ inactive: !legendSelected['订单数'] }"
          :tabindex="legendSelected['订单数'] ? 0 : -1"
          @click="toggleLegend('订单数')"
        >
          <span class="legend-marker"></span>
          <span class="legend-label">订单数(笔)</span>
        </div>
        <div 
          class="legend-item legend-order-amount" 
          :class="{ inactive: !legendSelected['订单码洋'] }"
          :tabindex="legendSelected['订单码洋'] ? 0 : -1"
          @click="toggleLegend('订单码洋')"
        >
          <span class="legend-marker"></span>
          <span class="legend-label">订单码洋(万元)</span>
        </div>
      </div>
    </div>
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as echarts from 'echarts'

export default {
  name: 'OrderComparison',
  data() {
    return {
      chart: null,
      legendSelected: {
        '订单数': true,
        '订单码洋': true
      }
    }
  },
  computed: {
    ...mapState('order', ['orderComparison'])
  },
  mounted() {
    this.initChart()
    this.updateChart()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  watch: {
    'orderComparison.dates': {
      handler() {
        if (this.orderComparison.dates && Array.isArray(this.orderComparison.dates)) {
          this.updateChart()
        }
      },
      deep: true
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chartRef)
      
      const resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      
      window.addEventListener('resize', resizeHandler)
      
      // 清理事件监听器
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', resizeHandler)
      })
    },
    updateChart() {
      if (!this.chart || !this.orderComparison.dates || !Array.isArray(this.orderComparison.dates) || !this.orderComparison.dates.length) return

      // 原始数据源（使用 store 已聚合的“周区间”标签与配对数据）
      const datesRaw = this.orderComparison.dates
      const ordersSource = this.orderComparison.orders || []
      const amountsSource = this.orderComparison.amounts || []
      const compareYears = this.orderComparison.compareYears || []

      // 两条折线：订单数与码洋（万元），不区分往年
      const orderCount = ordersSource.map(item => item.currentYear)
      const orderAmountWan = amountsSource.map(item => item.currentYear / 10000)

      // 将区间或原始日期字符串格式化为图示的 M.D（不补零）
      const formatDateLabel = (label) => {
        if (!label || typeof label !== 'string') return label
        // 可能是 "YYYY.M.DD~YYYY.M.DD" 或 "YYYY.MM.DD" 或其他
        const part = label.includes('~') ? label.split('~')[1] : label
        const segs = part.split('.')
        if (segs.length >= 3) {
          const m = parseInt(segs[1], 10)
          const d = parseInt(segs[2], 10)
          if (!isNaN(m) && !isNaN(d)) return `${m}.${d}`
        }
        // 兼容类似 "04月" 的 Mock 格式，直接返回原值
        return label
      }
      const datesFormatted = datesRaw.map(formatDateLabel)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: { color: '#999', width: 1 }
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#1890ff',
          textStyle: { color: '#ffffff' },
          formatter: (params) => {
            const idx = (params && params[0]) ? params[0].dataIndex : 0
            const dateLabel = datesRaw[idx] || ''
            const count = ordersSource[idx]?.currentYear ?? 0
            const amount = amountsSource[idx]?.currentYear ?? 0
            const lastCount = ordersSource[idx]?.lastYear
            const lastAmount = amountsSource[idx]?.lastYear

            // 订单笔数单位转换逻辑
            const formatOrderCount = (count) => {
              if (count >= 10000) {
                return `${Math.round(count / 10000)}(万笔)`
              } else {
                return `${count}(笔)`
              }
            }

            // 码洋单位转换逻辑
            const formatAmount = (amount) => {
              if (amount >= 10000) {
                return `${Math.round(amount / 10000)}(万元)`
              } else {
                return `${amount}(元)`
              }
            }

            const compareYear = compareYears[idx] || (new Date().getFullYear() - 1)
            const dotStyle = 'width:6px;height:6px;border-radius:50%;background:#2391FF;display:inline-block;margin-right:8px;'
            const wrapStyle = 'color:#ffffff;font-size:12px;line-height:1.2;padding:10px 12px;box-sizing:border-box;'
            const titleStyle = 'font-size:12px;margin-bottom:8px;opacity:0.8;'
            const rowStyle = 'display:flex;align-items:center;margin-bottom:6px;'
            const subTitleStyle = 'color:#ffffff;opacity:0.8;margin:8px 0 6px;'

            return (
              `<div style="${wrapStyle}">
                <div style="${titleStyle}">${dateLabel}</div>
                <div style="${rowStyle}"><span style="${dotStyle}"></span><span>订单数/码洋&nbsp;&nbsp;<b>${formatOrderCount(count)}</b>&nbsp;/&nbsp;<b>${formatAmount(amount)}</b></span></div>
                ${ (lastCount != null || lastAmount != null) ? `
                  <div style="${subTitleStyle}">${compareYear}年同期</div>
                  <div style="${rowStyle}"><span style="${dotStyle}"></span><span>订单数/码洋&nbsp;&nbsp;<b>${formatOrderCount(lastCount ?? 0)}</b>&nbsp;/&nbsp;<b>${formatAmount(lastAmount ?? 0)}</b></span></div>
                ` : '' }
              </div>`
            )
          },
          extraCssText: 'border:none;box-shadow:none;border-radius:8px;padding:0;'
        },
        legend: {
          show: false,
          selected: this.legendSelected
        },
        grid: {
          left: '2%',
          right: '2%',
          // 增加底部留白，避免标签被裁剪
          bottom: '14%',
          top: '5%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: datesFormatted,
            boundaryGap: true,
            axisPointer: { label: { show: false } },
            axisLabel: {
              color: '#FFFFFF',
              fontSize: 12,
              // 显示所有刻度标签，不隐藏重叠
              interval: 0,
              hideOverlap: false,
              // 保持水平显示，提高可读性
              rotate: 0,
              // 增加与轴线的间距，避免贴边
              margin: 12,
              formatter: (value) => value
            },
            axisTick: { show: false, alignWithLabel: true },
            axisLine: { lineStyle: { color: 'rgba(24, 144, 255, 0.3)' } }
          }
        ],
        yAxis: [
          {
            type: 'value',
            position: 'left',
            axisLabel: { color: '#FFFFFF', fontSize: 12, formatter: '{value}' },
            axisLine: { show: false },
            splitLine: { lineStyle: { color: 'rgba(24, 144, 255, 0.1)' } }
          },
          {
            type: 'value',
            position: 'right',
            axisLabel: { color: '#FFFFFF', fontSize: 12, formatter: '{value}' },
            axisLine: { show: false },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: '订单数',
            type: 'line',
            yAxisIndex: 0,
            showSymbol: false,
            data: orderCount,
            lineStyle: { color: '#2391FF', width: 3 },
            itemStyle: { color: '#2391FF' },
            smooth: true
          },
          {
            name: '订单码洋',
            type: 'line',
            yAxisIndex: 1,
            showSymbol: false,
            data: orderAmountWan,
            lineStyle: { color: '#5AD8A6', width: 3 },
            itemStyle: { color: '#5AD8A6' },
            smooth: true
          }
        ]
      }

      this.chart.setOption(option)
      this.bindLegendEvents()
    }
    ,
    // x轴与 tooltip 已直接使用 store 的“周区间”标签，无需额外格式化
    toggleLegend(name) {
      if (!this.chart) return
      try {
        this.chart.dispatchAction({ type: 'legendToggleSelect', name })
      } catch (error) {
        console.error('Failed to toggle legend', error)
      }
    },
    bindLegendEvents() {
      if (!this.chart) return
      this.chart.off('legendselectchanged')
      this.chart.on('legendselectchanged', (params) => {
        this.legendSelected = { ...params.selected }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.order-comparison {
  height: 100%;
  display: flex;
  flex-direction: column;

  .module-header {
    display: flex;
    align-items: center; // 垂直居中对齐标题与图例
    gap: 12px;
    .legend-outside {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 10px;
      .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #ffffff;
        transition: opacity 0.2s ease, filter 0.2s ease, color 0.2s ease;
        cursor: pointer;
        user-select: none;
        .legend-marker {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          display: inline-block;
        }
      }
      .legend-item.inactive .legend-marker {
        filter: grayscale(100%) brightness(0.85);
      }
      .legend-item.inactive .legend-label {
        color: rgba(255, 255, 255, 0.6);
      }
      .legend-order-count .legend-marker { background: #2391FF; }
      .legend-order-amount .legend-marker { background: #5AD8A6; }
    }
  }

  .chart-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 270px;
    position: relative;

    .chart {
      width: 100%;
      height: 290px;
    }
  }
}
</style>